import { networkStatus } from "./lib/network_status"

const REQUEST_TIMEOUT = 10000 // 10s — switch to offline if no response

function getCsrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute("content") : ""
}

function offlineError() {
  const err = new Error("Offline")
  err.isOffline = true
  return err
}

async function apiFetch(path, options = {}) {
  // In offline mode — no network requests at all
  if (!networkStatus.online) throw offlineError()

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

  const headers = {
    "Accept": "application/json",
    "X-CSRF-Token": getCsrfToken(),
    ...options.headers
  }

  try {
    const response = await fetch(path, {
      credentials: "same-origin",
      ...options,
      headers,
      signal: controller.signal
    })
    clearTimeout(timeoutId)

    networkStatus.reportSuccess()

    if (response.status === 401) {
      window.location.href = "/pwa/login"
      throw new Error("Unauthorized")
    }

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      const err = new Error(data.error || `API error: ${response.status}`)
      err.status = response.status
      err.data = data
      throw err
    }

    return response.json()
  } catch (e) {
    clearTimeout(timeoutId)
    if (e.name === "AbortError") {
      networkStatus.reportFailure()
      const err = new Error("Request timeout")
      err.isTimeout = true
      err.isOffline = true
      throw err
    }
    if (e.message === "Failed to fetch" || e.message === "NetworkError when attempting to fetch resource." || e.message === "Load failed") {
      networkStatus.reportFailure()
      e.isOffline = true
    }
    throw e
  }
}

export async function apiGet(path) {
  return apiFetch(path)
}

export async function apiPost(path, body) {
  return apiFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  })
}

export async function apiPut(path, body) {
  return apiFetch(path, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  })
}

export async function apiUpload(path, formData) {
  // In offline mode — no network requests at all
  if (!networkStatus.online) throw offlineError()

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 60000) // 60s for uploads

  try {
    const response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": getCsrfToken()
      },
      body: formData,
      signal: controller.signal
    })
    clearTimeout(timeoutId)

    networkStatus.reportSuccess()

    if (response.status === 401) {
      window.location.href = "/pwa/login"
      throw new Error("Unauthorized")
    }

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      const err = new Error(data.error || `API error: ${response.status}`)
      err.status = response.status
      err.data = data
      throw err
    }

    return response.json()
  } catch (e) {
    clearTimeout(timeoutId)
    if (e.name === "AbortError") {
      networkStatus.reportFailure()
      const err = new Error("Request timeout")
      err.isTimeout = true
      err.isOffline = true
      throw err
    }
    if (e.message === "Failed to fetch" || e.message === "NetworkError when attempting to fetch resource." || e.message === "Load failed") {
      networkStatus.reportFailure()
      e.isOffline = true
    }
    throw e
  }
}

export async function apiDelete(path) {
  return apiFetch(path, { method: "DELETE" })
}

// Currency metadata cache — initialized with hardcoded fallback,
// overwritten when /api/currencies responds. Adding a new currency
// on the backend is enough; no frontend redeploy needed.
const currencyMeta = {}

export function setCurrencyMeta(currencies) {
  for (const c of currencies) {
    currencyMeta[c.code] = { symbol: c.symbol, decimals: c.decimals, type: c.type }
  }
}

function getCurrencyInfo(code) {
  return currencyMeta[code]
}

export function decimalsFor(code) {
  const info = getCurrencyInfo(code)
  return info ? info.decimals : 2
}

export function formatMoney(amount, currency = "RUB") {
  const locale = window.APP_LOCALE === "ru" ? "ru-RU" : "en-US"
  const info = getCurrencyInfo(currency)

  if (info && info.type === "crypto") {
    let minDecimals = 0
    let maxDecimals = info.decimals
    // For tiny amounts (e.g. 0.00001176 BTC), ensure at least one significant digit is shown
    if (amount !== 0 && Math.abs(amount) < 1) {
      const magnitude = -Math.floor(Math.log10(Math.abs(amount)))
      if (magnitude > 0) {
        minDecimals = Math.min(magnitude + 2, maxDecimals)
      }
    }
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: minDecimals,
      maximumFractionDigits: maxDecimals
    }).format(amount)
    return `${formatted} ${info.symbol}`
  }

  // Fiat — use Intl with server-provided decimals when available
  const maxDecimals = info ? info.decimals : 2
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals
  }).format(amount)
}

export function t(key) {
  const keys = key.split(".")
  let value = window.APP_I18N
  for (const k of keys) {
    if (!value) return key
    value = value[k]
  }
  return value || key
}

export function showAiConsentModal() {
  return new Promise((resolve) => {
    const overlay = document.createElement("div")
    overlay.id = "ai-consent-modal"
    overlay.style.cssText = "position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.6);padding:16px;"
    overlay.innerHTML = `
      <div style="background:#fff;border-radius:16px;padding:24px;max-width:400px;width:100%;text-align:center;" class="dark:bg-slate-800">
        <div style="font-size:32px;margin-bottom:12px;">🤖</div>
        <h2 style="font-size:18px;font-weight:700;margin:0 0 8px;">AI-обработка данных</h2>
        <p style="font-size:14px;line-height:1.5;opacity:0.7;margin:0 0 16px;">
          Для\u00A0работы этой функции ваши данные будут обработаны с\u00A0помощью AI-сервисов (OpenAI, Google Gemini). Серверы расположены за\u00A0пределами РФ. Данные используются только для\u00A0обработки запроса и\u00A0не\u00A0сохраняются на\u00A0стороне провайдеров.
        </p>
        <p style="font-size:13px;line-height:1.4;opacity:0.5;margin:0 0 20px;">
          Это требование ст.\u00A012 Федерального закона №152-ФЗ о\u00A0трансграничной передаче данных. Подробнее\u00A0— в\u00A0<a href="/privacy" target="_blank" style="color:#6366F1;text-decoration:underline;">политике конфиденциальности</a>.
        </p>
        <button id="ai-consent-accept-btn" style="width:100%;padding:14px;border:none;border-radius:12px;background:linear-gradient(to right,#6366F1,#9333EA);color:#fff;font-size:16px;font-weight:600;cursor:pointer;">
          Принимаю
        </button>
      </div>
    `
    document.body.appendChild(overlay)

    document.getElementById("ai-consent-accept-btn").addEventListener("click", async function() {
      const btn = this
      btn.disabled = true
      btn.textContent = "..."
      try {
        await apiPost("/api/consent/ai", {})
        overlay.remove()
        resolve(true)
      } catch {
        btn.disabled = false
        btn.textContent = "Принимаю"
      }
    })
  })
}
